import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  navigationPreferenceService,
  type SocietyNavigationPreferenceResponse,
} from '@/services/navigationPreferenceService';
import { useAuthStore } from '@/store/authStore';

export const NAVIGATION_PREFERENCES_QUERY_KEY = 'navigation-preferences';

export function useNavigationPreferences(societyId?: string) {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  const effectiveTenantId = (societyId || tenant?.id || tenant?._id || user?.tenantId) ?? undefined;
  const isPlatformUser = isSuperAdmin();

  return useQuery<SocietyNavigationPreferenceResponse>({
    queryKey: [NAVIGATION_PREFERENCES_QUERY_KEY, effectiveTenantId],
    queryFn: () => navigationPreferenceService.getPreferences(effectiveTenantId || undefined),
    enabled: Boolean(effectiveTenantId) && !isPlatformUser,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateNavigationPreferences(societyId?: string) {
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const effectiveTenantId = (societyId || tenant?.id || tenant?._id || user?.tenantId) ?? undefined;

  return useMutation({
    mutationFn: (payload: {
      version?: number;
      mainNavigation: Array<{
        menuId: string;
        order: number;
        visible?: boolean;
        children?: Array<{
          menuId: string;
          order: number;
          visible?: boolean;
        }>;
      }>;
      secondaryNavigation?: Array<{
        menuId: string;
        order: number;
        visible?: boolean;
      }>;
    }) => navigationPreferenceService.updatePreferences(payload, effectiveTenantId || undefined),
    onSuccess: (data) => {
      queryClient.setQueryData([NAVIGATION_PREFERENCES_QUERY_KEY, effectiveTenantId], data);
      queryClient.invalidateQueries({ queryKey: [NAVIGATION_PREFERENCES_QUERY_KEY] });
    },
  });
}

export function useResetNavigationPreferences(societyId?: string) {
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const effectiveTenantId = (societyId || tenant?.id || tenant?._id || user?.tenantId) ?? undefined;

  return useMutation({
    mutationFn: () => navigationPreferenceService.resetPreferences(effectiveTenantId || undefined),
    onSuccess: (data) => {
      queryClient.setQueryData([NAVIGATION_PREFERENCES_QUERY_KEY, effectiveTenantId], data);
      queryClient.invalidateQueries({ queryKey: [NAVIGATION_PREFERENCES_QUERY_KEY] });
    },
  });
}
